import { prepareInspectionImage } from '../lib/image';
import { validateReopenNote, validateWaiver } from '../lib/close';
import { supabase } from '../lib/supabase';
import type { ActionRecord } from '../types/actions';
import type { ClosureEventRecord } from '../types/close';
import type { EvidenceRecord, IssueRecord } from '../types/issues';

const ACTION_SELECT =
  'id, issue_id, requested_action, accountable_party, accountable_party_name, priority, due_date, follow_up_date, status, created_at, updated_at';

export async function loadClosureEvents(
  ownerId: string,
  actionId: string,
): Promise<ClosureEventRecord[]> {
  const { data, error } = await supabase
    .from('closure_events')
    .select('id, issue_id, action_id, closure_evidence_id, waiver_acknowledged, waiver_reason, closure_note, created_at')
    .eq('owner_id', ownerId)
    .eq('action_id', actionId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []) as ClosureEventRecord[];
}

export async function loadClosureEvidence(
  ownerId: string,
  issueId: string,
): Promise<EvidenceRecord[]> {
  const { data, error } = await supabase
    .from('evidence')
    .select('id, issue_id, storage_path, file_name, mime_type, size_bytes, evidence_type, note, captured_at, created_at')
    .eq('owner_id', ownerId)
    .eq('issue_id', issueId)
    .eq('evidence_type', 'closure')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []) as EvidenceRecord[];
}

export async function uploadClosureEvidence(
  ownerId: string,
  assessmentId: string,
  issueId: string,
  file: File,
  note: string | null,
): Promise<EvidenceRecord> {
  const prepared = await prepareInspectionImage(file);
  const extension = prepared.name.split('.').pop() || 'jpg';
  const path = `${ownerId}/${assessmentId}/${issueId}/closure/${crypto.randomUUID()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from('inspection-evidence')
    .upload(path, prepared, {
      cacheControl: '3600',
      contentType: prepared.type,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from('evidence')
    .insert({
      owner_id: ownerId,
      issue_id: issueId,
      storage_path: path,
      file_name: prepared.name,
      mime_type: prepared.type,
      size_bytes: prepared.size,
      evidence_type: 'closure',
      note: note?.trim() || null,
      captured_at: new Date().toISOString(),
    })
    .select('id, issue_id, storage_path, file_name, mime_type, size_bytes, evidence_type, note, captured_at, created_at')
    .single();

  if (error) {
    await supabase.storage.from('inspection-evidence').remove([path]);
    throw error;
  }

  return data as EvidenceRecord;
}

async function reloadAction(ownerId: string, actionId: string): Promise<ActionRecord> {
  const { data, error } = await supabase
    .from('actions')
    .select(ACTION_SELECT)
    .eq('id', actionId)
    .eq('owner_id', ownerId)
    .single();
  if (error) throw error;
  return data as ActionRecord;
}

export async function completeCloseWithEvidence(
  ownerId: string,
  issue: IssueRecord,
  action: ActionRecord,
  evidenceId: string,
  closureNote: string,
): Promise<ActionRecord> {
  if (action.status !== 'rectification_claimed') {
    throw new Error('Close can start only after rectification has been claimed and rechecked.');
  }

  const { error } = await supabase.rpc('complete_action_closure', {
    p_action_id: action.id,
    p_issue_id: issue.id,
    p_closure_evidence_id: evidenceId,
    p_waiver_acknowledged: false,
    p_waiver_reason: null,
    p_closure_note: closureNote.trim() || null,
  });
  if (error) throw error;
  return reloadAction(ownerId, action.id);
}

export async function completeCloseWithWaiver(
  ownerId: string,
  issue: IssueRecord,
  action: ActionRecord,
  acknowledged: boolean,
  reason: string,
  closureNote: string,
): Promise<ActionRecord> {
  if (action.status !== 'rectification_claimed') {
    throw new Error('Close can start only after rectification has been claimed and rechecked.');
  }

  const validationError = validateWaiver(acknowledged, reason);
  if (validationError) throw new Error(validationError);

  const { error } = await supabase.rpc('complete_action_closure', {
    p_action_id: action.id,
    p_issue_id: issue.id,
    p_closure_evidence_id: null,
    p_waiver_acknowledged: true,
    p_waiver_reason: reason.trim(),
    p_closure_note: closureNote.trim() || null,
  });
  if (error) throw error;
  return reloadAction(ownerId, action.id);
}

export async function reopenAfterRecheck(
  ownerId: string,
  issue: IssueRecord,
  action: ActionRecord,
  note: string,
): Promise<ActionRecord> {
  if (action.status !== 'rectification_claimed') {
    throw new Error('Only a rectification claim can be reopened after recheck.');
  }
  const validationError = validateReopenNote(note);
  if (validationError) throw new Error(validationError);

  const { error } = await supabase.rpc('reopen_action_after_recheck', {
    p_action_id: action.id,
    p_issue_id: issue.id,
    p_note: note.trim(),
  });
  if (error) throw error;
  return reloadAction(ownerId, action.id);
}
